<template>
  <n-card title="Dépenses par catégorie">
    <n-space vertical>
      <n-space>
        <n-date-picker
          v-model:value="startTimestamp"
          type="date"
          placeholder="Date de début"
          @update:value="onDateChange"
        />
        <n-date-picker
          v-model:value="endTimestamp"
          type="date"
          placeholder="Date de fin"
          @update:value="onDateChange"
        />
      </n-space>

      <div v-if="hasData" style="max-width: 420px; margin: 0 auto">
        <Pie :data="chartData" :options="chartOptions" />
      </div>
      <n-empty v-else description="Aucune dépense sur cette période" />

      <n-table v-if="hasData" :bordered="false" size="small">
        <thead>
          <tr>
            <th>Catégorie</th>
            <th style="text-align: right">Montant</th>
            <th style="text-align: right">%</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(amount, category) in sortedData" :key="category">
            <td>
              <n-tag size="small" :color="getCategoryColor(category)">{{ category }}</n-tag>
            </td>
            <td style="text-align: right">{{ formatCurrency(amount) }}</td>
            <td style="text-align: right">{{ ((amount / total) * 100).toFixed(1) }}%</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td><strong>Total</strong></td>
            <td style="text-align: right"><strong>{{ formatCurrency(total) }}</strong></td>
            <td></td>
          </tr>
        </tfoot>
      </n-table>
    </n-space>
  </n-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Pie } from 'vue-chartjs';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useExpenseStore } from '../stores/expenseStore';

ChartJS.register(ArcElement, Tooltip, Legend);

const expenseStore = useExpenseStore();

const CATEGORY_COLORS = {
  Alimentation:  { color: '#3b8c5a', textColor: '#fff', borderColor: '#3b8c5a' },
  Transport:     { color: '#2563eb', textColor: '#fff', borderColor: '#2563eb' },
  Logement:      { color: '#7c3aed', textColor: '#fff', borderColor: '#7c3aed' },
  'Santé':       { color: '#dc2626', textColor: '#fff', borderColor: '#dc2626' },
  Loisirs:       { color: '#d97706', textColor: '#fff', borderColor: '#d97706' },
  Abonnements:   { color: '#0891b2', textColor: '#fff', borderColor: '#0891b2' },
  Maison:        { color: '#b45309', textColor: '#fff', borderColor: '#b45309' },
  Autre:         { color: '#6b7280', textColor: '#fff', borderColor: '#6b7280' },
};

const getCategoryColor = (category) => CATEGORY_COLORS[category] ?? CATEGORY_COLORS.Autre;

const now = new Date();
const startTimestamp = ref(new Date(now.getFullYear(), 0, 1).getTime());
const endTimestamp = ref(new Date(now.getFullYear(), 11, 31).getTime());

const toIso = (ts) => new Date(ts).toISOString().split('T')[0];

const load = () => {
  expenseStore.fetchCategoryData(toIso(startTimestamp.value), toIso(endTimestamp.value));
};

const onDateChange = () => load();

onMounted(load);

const hasData = computed(() => {
  const d = expenseStore.categoryData;
  return d && Object.keys(d).length > 0;
});

const sortedData = computed(() => {
  if (!expenseStore.categoryData) return {};
  return Object.fromEntries(
    Object.entries(expenseStore.categoryData).sort(([, a], [, b]) => b - a)
  );
});

const total = computed(() =>
  Object.values(expenseStore.categoryData ?? {}).reduce((s, v) => s + v, 0)
);

const chartData = computed(() => {
  const labels = Object.keys(sortedData.value);
  return {
    labels,
    datasets: [{
      data: Object.values(sortedData.value),
      backgroundColor: labels.map(l => CATEGORY_COLORS[l]?.color ?? '#6b7280'),
      borderWidth: 0,
    }],
  };
});

const chartOptions = {
  responsive: true,
  plugins: {
    legend: { position: 'bottom', labels: { color: '#ccc' } },
    tooltip: {
      callbacks: {
        label: (ctx) => ` ${ctx.label}: ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(ctx.raw)}`,
      },
    },
  },
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
</script>
